import { Droplets, Sprout, Sun, type LucideIcon } from "lucide-react";

export type AssetSlug = "solar" | "planty" | "aqua";

export type MilestoneMetric = {
  label: string;
  value: string;
  sub?: string;
};

export type AssetConfig = {
  slug: AssetSlug;
  name: string;
  symbol: string;
  tagline: string;
  /** Vibrant neon accent (OKLCH) */
  color: string;
  /** Same hue, softer (OKLCH) */
  colorSoft: string;
  /** Background glow gradient */
  glow: string;
  icon: LucideIcon;
  /** Hardcoded project treasury wallet for direct funding (DO NOT route to a DEX). */
  treasury: string;
  /** Fixed reference value: 1 asset token = USD price during this milestone. */
  refUsdPerToken: number;
  /** SOL/USD reference price used for the simulation (replaceable by a live oracle). */
  solUsdRef: number;
  milestone: {
    phase: string;
    title: string;
    body: string;
    raisedUsd: number;
    goalUsd: number;
    metrics: MilestoneMetric[];
  };
};

export const VP_ASSETS: Record<AssetSlug, AssetConfig> = {
  solar: {
    slug: "solar",
    name: "SolarPando",
    symbol: "SOLAR",
    tagline: "Off-grid microgrid energy backing physical MEAL24 kitchens.",
    color: "oklch(0.86 0.16 90)", // radiant amber
    colorSoft: "oklch(0.86 0.16 90 / 0.18)",
    glow: "linear-gradient(135deg, oklch(0.86 0.16 90 / 0.45), oklch(0.78 0.18 60 / 0.4))",
    icon: Sun,
    treasury: "SoLArPnDoTrEAsUrY1111111111111111111111111111",
    refUsdPerToken: 0.10,
    solUsdRef: 150,
    milestone: {
      phase: "Phase 1",
      title: "Local Solar Grid Setup — Thailand Sandbox",
      body: "Deploying the first 12 kWp rooftop array + 30 kWh battery bank to power the MEAL24 anchor kitchen completely off-grid.",
      raisedUsd: 18_420,
      goalUsd: 45_000,
      metrics: [
        { label: "Live array output", value: "8.4 kW", sub: "rolling 24h avg" },
        { label: "Battery reserve", value: "62%", sub: "30 kWh bank" },
        { label: "Panels installed", value: "24 / 60", sub: "Tier-1 mono-Si" },
        { label: "Kitchens powered", value: "1", sub: "SFN Pilot Kitchen" },
      ],
    },
  },
  planty: {
    slug: "planty",
    name: "PlantyPando",
    symbol: "PLANTY",
    tagline: "Tokenized forwards on verified reforestation and ingredient supply.",
    color: "oklch(0.84 0.16 160)", // botanical green
    colorSoft: "oklch(0.84 0.16 160 / 0.18)",
    glow: "linear-gradient(135deg, oklch(0.84 0.16 160 / 0.45), oklch(0.76 0.18 150 / 0.4))",
    icon: Sprout,
    treasury: "PLAnTyPnDoTrEAsUrY2222222222222222222222222222",
    refUsdPerToken: 0.05,
    solUsdRef: 150,
    milestone: {
      phase: "Phase 1",
      title: "Reforestation Hub — Thailand Sandbox corridor",
      body: "Planting 5,000 native fruit and shade trees feeding directly into the MEAL24 ingredient supply chain. Every tree is geo-logged and verified.",
      raisedUsd: 9_750,
      goalUsd: 30_000,
      metrics: [
        { label: "Trees planted", value: "1,624", sub: "verified · geo-tagged" },
        { label: "Species mix", value: "11", sub: "native + perennial" },
        { label: "Hectares secured", value: "3.2 ha", sub: "long-lease, 25y" },
        { label: "Ingredient lines", value: "4", sub: "feeding MEAL24" },
      ],
    },
  },
  aqua: {
    slug: "aqua",
    name: "AquaPando",
    symbol: "AQUA",
    tagline: "Filtered-water reservoirs securing physical kitchen production.",
    color: "oklch(0.8 0.14 230)", // deep cyan
    colorSoft: "oklch(0.8 0.14 230 / 0.18)",
    glow: "linear-gradient(135deg, oklch(0.8 0.14 230 / 0.45), oklch(0.72 0.16 245 / 0.4))",
    icon: Droplets,
    treasury: "AqUaPnDoTrEAsUrY3333333333333333333333333333",
    refUsdPerToken: 0.08,
    solUsdRef: 150,
    milestone: {
      phase: "Phase 1",
      title: "Filtered Water Reservoir — Thailand Sandbox Hub",
      body: "Installing a 20,000 L UV-filtered reservoir and gravity-fed distribution to guarantee daily kitchen production through dry seasons.",
      raisedUsd: 6_200,
      goalUsd: 22_000,
      metrics: [
        { label: "Reservoir capacity", value: "8,200 L", sub: "of 20,000 L" },
        { label: "Daily throughput", value: "1.4 m³", sub: "kitchen + community" },
        { label: "Filter stages", value: "5", sub: "sediment → UV" },
        { label: "Households served", value: "42", sub: "in addition to kitchen" },
      ],
    },
  },
};

export const VP_ASSET_LIST: AssetConfig[] = [VP_ASSETS.solar, VP_ASSETS.planty, VP_ASSETS.aqua];
